import { useEffect, useState } from "react";
import { API_BASE_URL } from "../utils/api";

/**
 * Loads JSON from API_BASE_URL + path. When the path changes (e.g. the
 * date filter changes), the previous in-flight request is aborted (AbortController);
 * there used to be no protection against this, so an older (slower) response
 * could overwrite a newer one on screen.
 *
 * path === null | undefined  ->  the fetch is skipped (e.g. while dates are not selected).
 * refreshKey - optional; when it changes (even if the path is IDENTICAL to before),
 * the data is fetched again. Used e.g. so that a click on an already selected period
 * ("Danas" while it is already active) refreshes the data instead of doing nothing.
 */
export function useApiData(path, refreshKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) {
      setData(null);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetch(`${API_BASE_URL}${path}`, { credentials: "include", signal: controller.signal })
      .then((res) => res.json())
      .then((json) => {
        // The server says the session is gone: App logs the user out and shows the login.
        if (json && json.success === false && json.error === "Niste prijavljeni") {
          window.dispatchEvent(new Event("auth-expired"));
        }
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Greška pri učitavanju podataka:", err);
        setError(err);
        setData(null);
        setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, refreshKey]);

  // If there is a path but there is neither data nor an error yet, it is still "loading" -
  // without this, "Nema podataka" flashed in the first frame before the fetch started.
  const effectiveLoading = loading || (Boolean(path) && data === null && error === null);
  return { data, loading: effectiveLoading, error };
}
